import React, { useState, useEffect, useRef } from 'react';
import MapFrame from '../../components/MapFrame';
import BlockSubtitle from '../../components/BlockSubtitle';
import MusicPlayer from '../../components/MusicPlayer';
import './Cosmos.css';

/* ── Helpers ──────────────────────────────────────────────────── */
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;
const src = u => !u ? null : u.startsWith('http') ? u : `${u}`;
const fmtDate = raw => {
  if (!raw) return 'ДД · ММ · ГГГГ';
  const d = new Date(raw);
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map(n => String(n).padStart(2, '0')).join(' · ');
};

/* ── Star field component ─────────────────────────────────────── */
// Pre-seeded star positions for deterministic rendering
const STAR_DATA_SM = [
  [8,12],[15,34],[23,67],[31,4],[42,89],[54,23],[63,56],[71,78],[82,11],[91,45],
  [5,58],[17,83],[29,31],[38,72],[47,15],[56,94],[65,41],[74,67],[85,28],[94,52],
  [11,77],[22,19],[33,88],[44,36],[55,61],[66,7],[77,84],[88,43],[99,26],[3,91],
];
const STAR_DATA_MD = [
  [10,25],[25,50],[40,75],[55,15],[70,60],[85,35],[18,80],[35,5],[60,90],[78,45],
  [6,40],[20,65],[45,20],[62,85],[80,10],[95,70],[30,55],[50,30],[68,75],[90,20],
];
const STAR_DATA_LG = [
  [14,18],[28,72],[52,38],[66,85],[79,52],[93,7],[7,63],[35,45],[58,15],[82,78],
];

function StarField() {
  return (
    <div className="cos-starfield" aria-hidden="true">
      <div className="cos-nebula cos-nebula--1" />
      <div className="cos-nebula cos-nebula--2" />
      <div className="cos-nebula cos-nebula--3" />
      {STAR_DATA_SM.map(([top, left], i) => (
        <span key={`sm-${i}`} className="cos-star cos-star--sm"
          style={{ top: `${top}%`, left: `${left}%`, animationDelay: `${(i * 0.37) % 4}s` }} />
      ))}
      {STAR_DATA_MD.map(([top, left], i) => (
        <span key={`md-${i}`} className="cos-star cos-star--md"
          style={{ top: `${top}%`, left: `${left}%`, animationDelay: `${(i * 0.53) % 5}s` }} />
      ))}
      {STAR_DATA_LG.map(([top, left], i) => (
        <span key={`lg-${i}`} className="cos-star cos-star--lg"
          style={{ top: `${top}%`, left: `${left}%`, animationDelay: `${(i * 0.71) % 3}s` }} />
      ))}
      <div className="cos-shooting-star" />
    </div>
  );
}

/* ── Constellation ornament ───────────────────────────────────── */
const ConstellationOrnament = () => (
  <svg className="cos-constellation" width="180" height="40" viewBox="0 0 180 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="0" y1="20" x2="40" y2="10" stroke="#f5d76e" strokeWidth="0.6" opacity="0.6"/>
    <line x1="40" y1="10" x2="80" y2="20" stroke="#f5d76e" strokeWidth="0.6" opacity="0.6"/>
    <line x1="80" y1="20" x2="100" y2="5" stroke="#f5d76e" strokeWidth="0.6" opacity="0.6"/>
    <line x1="100" y1="5" x2="130" y2="20" stroke="#f5d76e" strokeWidth="0.6" opacity="0.6"/>
    <line x1="130" y1="20" x2="160" y2="12" stroke="#f5d76e" strokeWidth="0.6" opacity="0.6"/>
    <line x1="160" y1="12" x2="180" y2="20" stroke="#f5d76e" strokeWidth="0.6" opacity="0.6"/>
    <circle cx="0"   cy="20" r="2.5" fill="#f5d76e" opacity="0.9"/>
    <circle cx="40"  cy="10" r="2"   fill="#e8a0c0" opacity="0.9"/>
    <circle cx="80"  cy="20" r="3"   fill="#f5d76e" opacity="1"/>
    <circle cx="100" cy="5"  r="2"   fill="#e8a0c0" opacity="0.9"/>
    <circle cx="130" cy="20" r="3.5" fill="#f5d76e" opacity="1"/>
    <circle cx="160" cy="12" r="2"   fill="#e8a0c0" opacity="0.9"/>
    <circle cx="180" cy="20" r="2.5" fill="#f5d76e" opacity="0.9"/>
  </svg>
);

/* ── Cosmic ring decoration ───────────────────────────────────── */
const CosmicRing = () => (
  <svg className="cos-ring" width="320" height="320" viewBox="0 0 320 320" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="160" cy="160" r="155" stroke="url(#cosRingGrad)" strokeWidth="1.5" strokeDasharray="8 4" opacity="0.6"/>
    <circle cx="160" cy="160" r="143" stroke="url(#cosRingGrad2)" strokeWidth="0.8" opacity="0.4"/>
    <circle cx="160" cy="5"   r="3" fill="#f5d76e" opacity="0.9"/>
    <circle cx="315" cy="160" r="2.5" fill="#e8a0c0" opacity="0.8"/>
    <circle cx="160" cy="315" r="3" fill="#7c5cbf" opacity="0.9"/>
    <circle cx="5"   cy="160" r="2.5" fill="#e8a0c0" opacity="0.8"/>
    <defs>
      <linearGradient id="cosRingGrad" x1="0" y1="0" x2="320" y2="320" gradientUnits="userSpaceOnUse">
        <stop offset="0%"   stopColor="#7c5cbf"/>
        <stop offset="50%"  stopColor="#f5d76e"/>
        <stop offset="100%" stopColor="#e8a0c0"/>
      </linearGradient>
      <linearGradient id="cosRingGrad2" x1="320" y1="0" x2="0" y2="320" gradientUnits="userSpaceOnUse">
        <stop offset="0%"   stopColor="#e8a0c0"/>
        <stop offset="100%" stopColor="#7c5cbf"/>
      </linearGradient>
    </defs>
  </svg>
);

/* ── Section divider ──────────────────────────────────────────── */
const CosmicDivider = () => (
  <div className="cos-divider" aria-hidden="true">
    <svg width="200" height="20" viewBox="0 0 200 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="0" y1="10" x2="80" y2="10" stroke="#7c5cbf" strokeWidth="0.8" opacity="0.5"/>
      <circle cx="90"  cy="10" r="2.5" fill="#f5d76e" opacity="0.9"/>
      <circle cx="100" cy="10" r="4"   fill="#7c5cbf" opacity="0.8"/>
      <circle cx="110" cy="10" r="2.5" fill="#e8a0c0" opacity="0.9"/>
      <line x1="120" y1="10" x2="200" y2="10" stroke="#7c5cbf" strokeWidth="0.8" opacity="0.5"/>
    </svg>
  </div>
);

/* ── Countdown ──────────────────────────────────────────────────── */
function Countdown({ date, accent }) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const ms = Math.max(0, new Date(date) - Date.now());
      setT({ d: Math.floor(ms / 86400000), h: Math.floor(ms % 86400000 / 3600000),
             m: Math.floor(ms % 3600000 / 60000), s: Math.floor(ms % 60000 / 1000) });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [date]);
  return (
    <div className="cos-countdown">
      {[['дней', t.d], ['часов', t.h], ['минут', t.m], ['секунд', t.s]].map(([u, v]) => (
        <div key={u} className="cos-cu">
          <span className="cos-cu-val" style={{ color: accent }}>{String(v).padStart(2, '0')}</span>
          <span className="cos-cu-label">{u}</span>
        </div>
      ))}
    </div>
  );
}

/* ── CalendarWidget ─────────────────────────────────────────────── */
function CalendarWidget({ date, accent, addBtn }) {
  if (!date) return null;
  const d = new Date(date), yr = d.getFullYear(), mo = d.getMonth(), day = d.getDate();
  const off = (new Date(yr, mo, 1).getDay() + 6) % 7;
  const dim = new Date(yr, mo + 1, 0).getDate();
  const prevDim = new Date(yr, mo, 0).getDate();
  const cells = [];
  for (let i = off - 1; i >= 0; i--) cells.push({ n: prevDim - i, cur: false });
  for (let i = 1; i <= dim; i++) cells.push({ n: i, cur: true });
  while (cells.length < 42) cells.push({ n: cells.length - off - dim + 1, cur: false });
  const MON = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
  return (
    <div className="cos-calendar">
      <div className="cos-cal-header">
        <span style={{ color: accent, fontWeight: 700 }}>{MON[mo]}</span>
        <span className="cos-cal-year">{yr}</span>
      </div>
      <div className="cos-cal-grid">
        {['Пн','Вт','Ср','Чт','Пт','Сб','Вс'].map(h => (
          <div key={h} className="cos-cal-head" style={{ color: accent }}>{h}</div>
        ))}
        {cells.map((c, i) => {
          const isDay = c.cur && c.n === day;
          return (
            <div key={i}
              className={`cos-cal-cell${!c.cur ? ' cos-cal-muted' : ''}${isDay ? ' cos-cal-today' : ''}`}
              style={isDay ? { background: accent } : {}}
            >{c.n}</div>
          );
        })}
      </div>
      {addBtn}
    </div>
  );
}

/* ── RSVPForm ───────────────────────────────────────────────────── */
function RSVPForm({ project, accent }) {
  const [status, setStatus] = useState('confirmed');
  const [name, setName] = useState('');
  const [survey, setSurvey] = useState({});
  const [message, setMessage] = useState('');
  const [done, setDone] = useState(false);
  const bc = project.block_content || {};

  if (done) return (
    <div className="cos-rsvp-done" style={{ borderColor: accent }}>
      <div className="cos-rsvp-done-icon">✦</div>
      <strong style={{ color: accent }}>Спасибо!</strong>
      <p>{status === 'confirmed' ? 'Ждём вас среди звёзд!' : 'Жаль, что не сможете прийти.'}</p>
    </div>
  );

  return (
    <form className="cos-rsvp-form" onSubmit={e => { e.preventDefault(); setDone(true); }}>
      <div className="cos-rsvp-options">
        {[{ v: 'confirmed', l: bc.rsvp?.yesText || 'Приду!' }, { v: 'declined', l: bc.rsvp?.noText || 'Не смогу прийти' }].map(o => (
          <label key={o.v}
            className={`cos-rsvp-opt${status === o.v ? ' active' : ''}`}
            style={status === o.v ? { background: accent, color: '#070714', borderColor: accent } : {}}
          >
            <input type="radio" name="rsvp" value={o.v} checked={status === o.v}
              onChange={() => setStatus(o.v)} style={{ display: 'none' }} />
            {o.l}
          </label>
        ))}
      </div>

      <input className="cos-input" placeholder="Ваше имя"
        value={name} onChange={e => setName(e.target.value)} required />

      {project.blocks?.includes('survey') && status === 'confirmed' && project.survey?.map(q => (
        <div key={q.id} className="cos-survey-q">
          <p className="cos-survey-label">{q.question}</p>
          {q.type === 'text'
            ? <textarea className="cos-input" rows={2} value={survey[q.id] || ''}
                onChange={e => setSurvey(s => ({ ...s, [q.id]: e.target.value }))} />
            : q.type === 'multiple'
            ? <div className="cos-survey-opts">{(q.options || []).map(opt => {
                const selected = Array.isArray(survey[q.id]) ? survey[q.id].includes(opt) : false;
                return (
                  <label key={opt}
                    className={`cos-rsvp-opt cos-rsvp-opt-sm${selected ? ' active' : ''}`}
                    style={selected ? { background: accent, color: '#070714', borderColor: accent } : {}}
                  >
                    <input type="checkbox" name={q.id} value={opt} checked={selected}
                      onChange={e => {
                        const cur = Array.isArray(survey[q.id]) ? survey[q.id] : [];
                        setSurvey(s => ({ ...s, [q.id]: e.target.checked ? [...cur, opt] : cur.filter(v => v !== opt) }));
                      }} style={{ display: 'none' }} />
                    {opt}
                  </label>
                );
              })}</div>
            : <div className="cos-survey-opts">{(q.options || []).map(opt => (
                <label key={opt}
                  className={`cos-rsvp-opt cos-rsvp-opt-sm${survey[q.id] === opt ? ' active' : ''}`}
                  style={survey[q.id] === opt ? { background: accent, color: '#070714', borderColor: accent } : {}}
                >
                  <input type="radio" name={q.id} value={opt}
                    onChange={() => setSurvey(s => ({ ...s, [q.id]: opt }))} style={{ display: 'none' }} />
                  {opt}
                </label>
              ))}</div>
          }
        </div>
      ))}

      {project.blocks?.includes('message') && (
        <div>
          {bc.message?.fieldLabel && (
            <p style={{ fontSize: '0.82rem', color: '#9090b0', marginBottom: 4 }}>{bc.message.fieldLabel}</p>
          )}
          <textarea className="cos-input" rows={3}
            placeholder={bc.message?.text || 'Ваши пожелания молодым...'}
            value={message} onChange={e => setMessage(e.target.value)} />
        </div>
      )}

      <button type="submit" className="cos-btn" style={{ background: accent }}>
        {bc.rsvp?.btnText || 'Подтвердить'}
      </button>
    </form>
  );
}

/* ── Wishlist ───────────────────────────────────────────────────── */
function Wishlist({ project, accent }) {
  const [reserved, setReserved] = useState({});
  const items = project.wishlist || [];
  if (!items.length) return null;
  return (
    <div className="cos-wishlist">
      {items.map(item => (
        <div key={item.id} className="cos-wish-item">
          <div className="cos-wish-info">
            <div className="cos-wish-name">{item.name}</div>
            {item.price && <div className="cos-wish-price" style={{ color: accent }}>{item.price} ₽</div>}
          </div>
          <div className="cos-wish-actions">
            {item.link && <a href={item.link} target="_blank" rel="noopener noreferrer" className="cos-btn-outline">Посмотреть</a>}
            {reserved[item.id]
              ? <button type="button" className="cos-btn cos-btn-muted"
                  onClick={() => setReserved(r => ({ ...r, [item.id]: false }))}>
                  ✓ Снять резерв
                </button>
              : <button type="button" className="cos-btn" style={{ background: accent, color: '#070714' }}
                  onClick={() => setReserved(r => ({ ...r, [item.id]: true }))}>
                  Зарезервировать
                </button>
            }
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── useReveal hook ─────────────────────────────────────────────── */
function useReveal(pageRef, blockKey) {
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const SEL = '.cos-reveal';
    const inPreview = !!root.closest('.lp-template-inner, .lp-template-outer');
    if (inPreview) {
      root.querySelectorAll(SEL).forEach(el => el.classList.add('cos-in'));
      return;
    }
    const els = root.querySelectorAll(SEL);
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('cos-in'); obs.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [pageRef, blockKey]);
}

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
export default function Cosmos({ project, design, isPreview }) {
  const pageRef = useRef(null);
  const blocks = project.blocks || [];
  useReveal(pageRef, blocks.join(','));

  const bc = project.block_content || {};
  const accentRaw = project.palette?.primary || design?.config?.primaryColor || '#f5d76e';
  const accent = accentRaw;

  const fontTitle = project.fonts?.title || design?.config?.fontTitle || null;
  const fontBody  = project.fonts?.body  || design?.config?.fontBody  || null;
  const fontStyle = {
    ...(fontTitle ? { '--f-disp': `'${fontTitle}', 'Unbounded', sans-serif` } : {}),
    ...(fontBody  ? { '--f-body': `'${fontBody}', 'Inter', sans-serif` }      : {}),
  };

  // Dynamic Google Fonts
  useEffect(() => {
    const id = 'cosmos-base-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id; link.rel = 'stylesheet';
      link.href = "https://fonts.googleapis.com/css2?family=Unbounded:wght@300;400;700;900&family=Inter:wght@300;400&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const families = [fontTitle, fontBody].filter(Boolean);
    if (!families.length) return;
    const id = 'cosmos-dynamic-fonts';
    let link = document.getElementById(id);
    if (!link) { link = document.createElement('link'); link.id = id; link.rel = 'stylesheet'; document.head.appendChild(link); }
    const query = families.map(f => `family=${encodeURIComponent(f)}:wght@400;600;700`).join('&');
    link.href = `https://fonts.googleapis.com/css2?${query}&display=swap`;
  }, [fontTitle, fontBody]);

  const photos    = project.cover_photos || (project.cover_photo ? [{ url: project.cover_photo }] : []);
  const invPhotos = project.invitation_photos || [];
  const invP1     = invPhotos[0] || photos[0] || null;
  const invP2     = invPhotos[1] || null;
  const dcPhotos  = project.dresscode_photos || [];

  const bride = project.bride || 'Невеста';
  const groom = project.groom || 'Жених';
  const venues = (project.venues || []).filter(v => (v.name || "").trim() || (v.address || "").trim());

  const tomorrowISO = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();
  const weddingDate = project.wedding_date || tomorrowISO;

  const HAS_MENU      = blocks.includes('menu');
  const HAS_CALENDAR  = blocks.includes('calendar');
  const HAS_COUNTDOWN = blocks.includes('countdown');
  const HAS_SCHEDULE  = blocks.includes('schedule');
  const HAS_MAP = blocks.includes('map') && ((p?.venues || project?.venues || []).some(v => v?.address?.trim()) || !!(p?.wedding_address || project?.wedding_address));
  const HAS_DRESSCODE = blocks.includes('palette');
  const HAS_RSVP      = blocks.includes('rsvp');
  const HAS_WISHLIST  = blocks.includes('wishlist');

  const allItems  = project.schedule || [];
  const optional  = allItems.filter(i => i.optional);
  const mandatory = allItems.filter(i => !i.optional);
  const mainItems = optional.length > 0 ? mandatory : allItems;

  return (
    <div className="cos-page" ref={pageRef} style={fontStyle}>
      <StarField />

      {/* ══ NAV ════════════════════════════════════════════════ */}
      {HAS_MENU && (
        <nav className="cos-nav">
          <span className="cos-nav-brand">{bride} &amp; {groom}</span>
          <div className="cos-nav-links">
            {HAS_SCHEDULE  && <a href="#schedule">Тайминг</a>}
            {HAS_MAP       && <a href="#map">Локация</a>}
            {HAS_DRESSCODE && <a href="#dresscode">Дресс-код</a>}
            {HAS_RSVP      && <a href="#rsvp">Анкета</a>}
          </div>
        </nav>
      )}

      {/* ══ HERO ═══════════════════════════════════════════════ */}
      <section className="cos-hero">
        <div className="cos-hero-content">
          {/* Photo in circular glowing frame */}
          <div className="cos-hero-photo-wrap cos-reveal">
            <CosmicRing />
            {invP1 ? (
              <div className="cos-hero-photo">
                <img src={src(invP1.url)} alt="" style={{ filter: flt(invP1), objectPosition: objPos(invP1) }} />
              </div>
            ) : (
              <div className="cos-hero-photo cos-hero-photo--empty">
                <span>Ваше фото</span>
              </div>
            )}
          </div>

          <div className="cos-hero-text cos-reveal" style={{ '--d': '.15s' }}>
            <div className="cos-hero-slogan">
              {bc.hero?.subtitle || project.wedding_slogan || 'Вы приглашены'}
            </div>
            <h1 className="cos-hero-names">
              <span className="cos-hero-name">{bride}</span>
              <ConstellationOrnament />
              <span className="cos-hero-amp">&amp;</span>
              <span className="cos-hero-name">{groom}</span>
            </h1>
            <div className="cos-hero-date" style={{ color: accent }}>
              {fmtDate(weddingDate)}
            </div>
            {project.wedding_time && (
              <div className="cos-hero-time">{project.wedding_time}</div>
            )}
            {HAS_RSVP && (
              <a href="#rsvp" className="cos-btn cos-hero-btn" style={{ background: accent, color: '#070714' }}>
                {bc.rsvp?.yesText || 'Заполнить анкету'} →
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ══ INVITATION ══════════════════════════════════════════ */}
      <section className="cos-section" id="invitation">
        <div className="cos-section-inner">
          <CosmicDivider />
          <h2 className="cos-section-title cos-reveal"
            dangerouslySetInnerHTML={{ __html: bc.message?.title || 'Дорогие наши!' }} />
          <div className="cos-invitation-grid">
            <div className="cos-invitation-text cos-reveal" style={{ '--d': '.05s' }}>
              <div className="cos-body-text">
                {bc.message?.text
                  ? <div dangerouslySetInnerHTML={{ __html: bc.message.text }} />
                  : (
                    <>
                      <p>Под покровом звёзд мы приглашаем вас разделить с нами этот <strong>волшебный день</strong> — день, когда два сердца соединятся навсегда.</p>
                      <p>Пусть этот вечер станет маленькой вечностью, полной любви, смеха и незабываемых воспоминаний.</p>
                      <p>Подтвердите своё присутствие <strong style={{ color: accent }}>через форму</strong> ниже.</p>
                    </>
                  )
                }
              </div>
              {HAS_RSVP && (
                <a href="#rsvp" className="cos-btn" style={{ background: accent, color: '#070714', marginTop: 16 }}>
                  {bc.rsvp?.btnText || 'Заполнить анкету'}
                </a>
              )}
            </div>

            <div className="cos-invitation-media cos-reveal" style={{ '--d': '.1s' }}>
              {invP2 ? (
                <div className="cos-photo-frame">
                  <img src={src(invP2.url)} alt="" style={{ filter: flt(invP2), objectPosition: objPos(invP2) }} />
                </div>
              ) : (
                <div className="cos-photo-frame cos-photo-frame--empty">
                  <span>Добавьте 2-е фото</span>
                </div>
              )}
              <CalendarWidget
                date={weddingDate}
                accent={accent}
                addBtn={HAS_CALENDAR && (() => {
                  const d = new Date(weddingDate);
                  const pad = n => String(n).padStart(2, '0');
                  const ymd = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
                  const title = encodeURIComponent(`Свадьба ${bride} & ${groom}`);
                  const href = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${ymd}/${ymd}`;
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer"
                      className="cos-cal-add-btn" style={{ background: accent, color: '#070714' }}>
                      📅 {bc.calendar?.title || 'Добавить в календарь'}
                    </a>
                  );
                })()}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ══ COUNTDOWN ═══════════════════════════════════════════ */}
      {HAS_COUNTDOWN && (
        <section className="cos-section cos-section--surface" id="countdown">
          <div className="cos-section-inner">
            <CosmicDivider />
            <h2 className="cos-section-title cos-reveal"
              dangerouslySetInnerHTML={{ __html: bc.countdown?.title || 'До звёздного часа' }} />
            <BlockSubtitle text={bc.countdown?.subtitle} className="cos-section-sub cos-reveal" style={{ '--d': '.05s' }} />
            <div className="cos-reveal" style={{ '--d': '.1s' }}>
              <Countdown date={weddingDate} accent={accent} />
            </div>
          </div>
        </section>
      )}

      {/* ══ SCHEDULE ════════════════════════════════════════════ */}
      {HAS_SCHEDULE && (
        <section className="cos-section" id="schedule">
          <div className="cos-section-inner">
            <CosmicDivider />
            <h2 className="cos-section-title cos-reveal"
              dangerouslySetInnerHTML={{ __html: bc.schedule?.title || 'Тайминг дня' }} />
            <BlockSubtitle text={bc.schedule?.subtitle} className="cos-section-sub cos-reveal" style={{ '--d': '.05s' }} />
            <div className="cos-timeline cos-reveal" style={{ '--d': '.1s' }}>
              {allItems.length > 0 ? (
                <>
                  {optional.length > 0 && (
                    <div className="cos-timeline-group">
                      <div className="cos-timeline-group-title">Необязательная часть</div>
                      {optional.map((item, i) => (
                        <div className="cos-timeline-item" key={i} style={{ '--i': i }}>
                          <div className="cos-timeline-dot" style={{ background: accent }} />
                          <div className="cos-timeline-time" style={{ color: accent }}>{item.time}</div>
                          <div className="cos-timeline-info">
                            <div className="cos-timeline-event">{item.event}</div>
                            {item.description && <div className="cos-timeline-desc">{item.description}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="cos-timeline-group">
                    {optional.length > 0 && <div className="cos-timeline-group-title">Основная программа</div>}
                    {mainItems.map((item, i) => (
                      <div className="cos-timeline-item" key={i} style={{ '--i': i }}>
                        <div className="cos-timeline-dot" style={{ background: accent }} />
                        <div className="cos-timeline-time" style={{ color: accent }}>{item.time}</div>
                        <div className="cos-timeline-info">
                          <div className="cos-timeline-event">{item.event}</div>
                          {item.description && <div className="cos-timeline-desc">{item.description}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                [['14:00','Сбор гостей'],['15:00','Церемония'],['16:00','Фотосессия'],['17:00','Банкет']].map(([t, e], i) => (
                  <div className="cos-timeline-item" key={i} style={{ opacity: 0.35, '--i': i }}>
                    <div className="cos-timeline-dot" style={{ background: '#7c5cbf' }} />
                    <div className="cos-timeline-time" style={{ color: '#7c5cbf' }}>{t}</div>
                    <div className="cos-timeline-event">{e}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      )}

      {/* ══ MAP ═════════════════════════════════════════════════ */}
      {HAS_MAP && (
        <section className="cos-section cos-section--surface" id="map">
          <div className="cos-section-inner">
            <CosmicDivider />
            <h2 className="cos-section-title cos-reveal"
              dangerouslySetInnerHTML={{ __html: bc.map?.title || 'Место торжества' }} />
            <BlockSubtitle text={bc.map?.subtitle} className="cos-section-sub cos-reveal" style={{ '--d': '.05s' }} />
            <div className="cos-venues cos-reveal" style={{ '--d': '.1s' }}>
              {venues.length > 0 ? venues.map((v, i) => (
                <div key={i} className="cos-venue-row">
                  <div className="cos-venue-pin">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill={accent}>
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <div className="cos-venue-info">
                    {v.label && <span className="cos-venue-label" style={{ color: accent }}>{v.label} · </span>}
                    {v.name && <span className="cos-venue-name">{v.name} · </span>}
                    <span>{v.address}</span>
                  </div>
                  <a href={`https://yandex.ru/maps/?text=${encodeURIComponent(v.address)}&z=16`}
                    target="_blank" rel="noopener noreferrer" className="cos-btn-outline">
                    Маршрут →
                  </a>
                </div>
              )) : (
                project.wedding_address ? (
                  <div className="cos-venue-row">
                    <div className="cos-venue-pin">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill={accent}>
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                      </svg>
                    </div>
                    <span className="cos-venue-info">{project.wedding_address}</span>
                  </div>
                ) : (
                  <div style={{ color: '#6060a0', fontSize: '0.9rem' }}>Адрес будет добавлен на шаге «Данные»</div>
                )
              )}
            </div>
            {(project.map_url || (project.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
            <div className="cos-map-frame cos-reveal" style={{ '--d': '.15s' }}>
              {(() => {
                if (project.map_url) return <MapFrame region={project.region} title="Карта" src={project.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                const withCoords = venues.filter(v => v.lat && v.lon);
                if (withCoords.length > 0) {
                  const clat = withCoords.reduce((s, v) => s + v.lat, 0) / withCoords.length;
                  const clon = withCoords.reduce((s, v) => s + v.lon, 0) / withCoords.length;
                  const pts  = withCoords.map(v => `${v.lon},${v.lat},pm2rdm`).join('~');
                  return <MapFrame region={project.region} title="Карта" src={`https://yandex.ru/map-widget/v1/?ll=${clon},${clat}&z=16&pt=${pts}&lang=ru_RU`} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                }
                const addr = venues.map(v => v.address).filter(Boolean).join(' — ') || project.wedding_address || '';
                const mapSrc = addr
                  ? `https://yandex.ru/map-widget/v1/?text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`
                  : `https://yandex.ru/map-widget/v1/?ll=37.617617,55.755811&z=12&lang=ru_RU`;
                return <MapFrame region={project.region} title="Карта" src={mapSrc} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
              })()}
            </div>
            )}
          </div>
        </section>
      )}

      {/* ══ DRESSCODE ═══════════════════════════════════════════ */}
      {HAS_DRESSCODE && (
        <section className="cos-section" id="dresscode">
          <div className="cos-section-inner">
            <CosmicDivider />
            <h2 className="cos-section-title cos-reveal"
              dangerouslySetInnerHTML={{ __html: bc.palette?.title || 'Дресс-код' }} />
            <BlockSubtitle text={bc.palette?.subtitle} className="cos-body-text cos-reveal" tag="div" style={{ '--d': '.05s', textAlign: 'center' }} />
            <div className="cos-swatches cos-reveal" style={{ '--d': '.1s' }}>
              {(project.dresscode_palette || []).map((sw, i) => {
                const c = sw.color || '#000';
                const hex = c.replace('#', '');
                const r = parseInt(hex.slice(0, 2), 16) || 0, g = parseInt(hex.slice(2, 4), 16) || 0, b = parseInt(hex.slice(4, 6), 16) || 0;
                const isLight = (r * 299 + g * 587 + b * 114) / 1000 > 128;
                return (
                  <div key={i} className="cos-swatch" style={{ background: c }}>
                    <span style={{ color: isLight ? '#0d0d2b' : '#e8e8ff' }}>{sw.label}</span>
                  </div>
                );
              })}
            </div>
            {dcPhotos.length > 0 && (
              <div className="cos-dresscode-photos cos-reveal" style={{ '--d': '.15s' }}>
                {dcPhotos.map((p, i) => (
                  <div key={i} className="cos-dresscode-photo">
                    <img src={src(p.url)} alt="" style={{ filter: flt(p), objectPosition: objPos(p) }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ══ RSVP ════════════════════════════════════════════════ */}
      {HAS_RSVP && (
        <section className="cos-section cos-section--surface" id="rsvp">
          <div className="cos-section-inner">
            <CosmicDivider />
            <h2 className="cos-section-title cos-reveal"
              dangerouslySetInnerHTML={{ __html: bc.rsvp?.title || 'Подтверждение' }} />
            <BlockSubtitle text={bc.rsvp?.subtitle} className="cos-section-sub cos-reveal" style={{ '--d': '.05s' }} />
            <div className="cos-rsvp-wrap cos-reveal" style={{ '--d': '.1s' }}>
              <RSVPForm project={project} accent={accent} />
            </div>
          </div>
        </section>
      )}

      {/* ══ WISHLIST ════════════════════════════════════════════ */}
      {HAS_WISHLIST && (
        <section className="cos-section" id="wishlist">
          <div className="cos-section-inner">
            <CosmicDivider />
            <h2 className="cos-section-title cos-reveal"
              dangerouslySetInnerHTML={{ __html: bc.wishlist?.title || 'Список желаний' }} />
            <BlockSubtitle text={bc.wishlist?.subtitle} className="cos-section-sub cos-reveal" style={{ '--d': '.05s' }} />
            <div className="cos-reveal" style={{ '--d': '.1s' }}>
              <Wishlist project={project} accent={accent} />
            </div>
          </div>
        </section>
      )}

      {/* ══ FOOTER ══════════════════════════════════════════════ */}
      <footer className="cos-footer">
        <CosmicDivider />
        <div className="cos-footer-inner">
          <div className="cos-footer-names">{bride} &amp; {groom}</div>
          <div className="cos-footer-date" style={{ color: accent }}>{fmtDate(weddingDate)}</div>
          {project.wedding_slogan && <div className="cos-footer-slogan">{project.wedding_slogan}</div>}
          <div className="cos-footer-stars" aria-hidden="true">
            {Array.from({ length: 7 }, (_, i) => (
              <span key={i} className="cos-footer-star" style={{ animationDelay: `${i * 0.4}s` }}>✦</span>
            ))}
          </div>
        </div>
      </footer>

      {/* ══ WATERMARK ═══════════════════════════════════════════ */}
      {project.payment_plan === 'free' && (
        <div className="cos-watermark">
          Создано на <strong>WeddingInvite</strong>
        </div>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={isPreview} />
      )}
    </div>
  );
}
